<template>
  <GalleryGridUI
    :total-count="totalGalleryMediaCount"
    :loaded-items="loadedGalleryMedia"
    :loading-count="loadingCount"
    :compact="compact"
    @load-more="loadMore"
    @grid-mounted="initSortIfEnabled"
  >
    <template v-slot:item="itemProps">
      <ServicedGalleryThumbnail
        @mouseenter="itemSelected(itemProps.item, itemProps.index)"
        style="cursor: pointer"
        @click="openLightBox(itemProps.item, itemProps.index)"
        :size="compact ? 0 : 128"
        :bottom-left-action="sortable ? thumbnailDragHandle : null"
        :medium="itemProps.item"
        :key="itemProps.item.id"
      />
    </template>
    <template v-slot:loading="loadingProps">
      <GalleryViewSkeletonLoader
        class="ignore-sort"
        :key="loadingProps.index"
      />
    </template>
  </GalleryGridUI>
</template>

<script lang="ts">
import { computed, defineComponent, inject, PropType, Ref, ref } from "vue"
import { GalleryThumbnailAction } from "features/gallery/thumbnail/GalleryThumbnail.vue"
import GalleryViewSkeletonLoader from "features/gallery/view/GalleryViewSkeletonLoader.vue"
import { LoadGalleryMediaFunction } from "features/gallery/view/GalleryView.vue"
import Sortable, { SortableEvent } from "sortablejs"
import { mdiDrag } from "@mdi/js"
import { LightboxService } from "features/gallery/lightbox/lightbox.service"
import { GalleryMedium } from "features/gallery/gallery.types"
import ServicedGalleryThumbnail from "features/gallery/service/ServicedGalleryThumbnail.vue"
import { GalleryService } from "features/gallery/service/gallery.service"
import { useIsBreakpointInNames } from "features/common/vuetify/useVuetify"
import { EventLightboxService } from "features/events/highlights/lightbox/event-lightbox.service"
import { PostLightboxService } from "features/posts/gallery/lightbox/post-lightbox.service"
import GalleryGridUI from "app/ui-primitives/gallery/GalleryGridUI.vue"

export default defineComponent({
  name: "GalleryViewGrid",
  components: {
    GalleryGridUI,
    ServicedGalleryThumbnail,
    GalleryViewSkeletonLoader,
  },
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    loadGalleryMedia: {
      type: Function as PropType<LoadGalleryMediaFunction>,
    },
    totalGalleryMedia: {
      type: Number,
      default: 0,
    },
    pageSize: {
      type: Number,
      default: 12,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    optimisticUpdate: {
      type: Boolean,
      default: false,
    },
    isHighlightGallery: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, context) {
    const loadedGalleryMedia: Ref<GalleryMedium[]> = ref([])
    const offset = ref(0)
    const isLoading = ref(false)

    const optimisticMediaCount = ref(0)
    const totalGalleryMediaCount = computed(() => {
      return props.totalGalleryMedia + optimisticMediaCount.value
    })

    if (props.optimisticUpdate) {
      const galleryService: GalleryService = inject("GalleryService")
      galleryService.registerRemoveListener((galleryId) => {
        loadedGalleryMedia.value = loadedGalleryMedia.value.filter(
          (medium) => medium.id != galleryId
        )
        optimisticMediaCount.value -= 1
      })
    }

    const itemsLeft = computed(() => {
      return totalGalleryMediaCount.value - loadedGalleryMedia.value.length
    })

    const nextAmountOfItems = computed(() => {
      const nextAmount = Math.min(itemsLeft.value, props.pageSize)
      if (nextAmount >= 0) {
        return nextAmount
      } else {
        return 0
      }
    })

    const _loadMore = async () => {
      const newGalleryMedia = await props.loadGalleryMedia(
        offset.value,
        nextAmountOfItems.value
      )
      loadedGalleryMedia.value = [
        ...loadedGalleryMedia.value,
        ...newGalleryMedia,
      ]
      offset.value += newGalleryMedia.length
    }

    const loadMore = async () => {
      if (isLoading.value == true || itemsLeft.value == 0) {
        return
      }
      isLoading.value = true
      try {
        await _loadMore()
      } catch (e) {
        console.error(e)
      } finally {
        isLoading.value = false
      }
    }

    const itemSelected = (galleryMedia: GalleryMedium, index: number) => {
      if (isDragging.value) return
      context.emit("mediumSelected", { ...galleryMedia, index: index })
    }

    // init first load
    if (props.loadGalleryMedia) {
      loadMore().then(() => {
        itemSelected(loadedGalleryMedia.value[0], 0)
      })
    }

    const isDragging = ref(false)

    const isSmallerDevice = useIsBreakpointInNames(["xs", "sm", "md"])

    const initSortIfEnabled = (mountedGrid: HTMLDivElement) => {
      if (!props.sortable) {
        return
      }
      new Sortable(mountedGrid, {
        animation: 350,
        sort: true,
        filter: ".ignore-sort",
        handle: isSmallerDevice.value ? undefined : ".gallery-drag-handle",
        chosenClass: "gallery-drag-chosen",
        delay: 300,
        delayOnTouchOnly: true,
        onStart: () => {
          isDragging.value = true
        },
        onEnd: (event: SortableEvent) => {
          isDragging.value = false
          const galleryMedium = loadedGalleryMedia.value[event.oldIndex]
          const _id = props.isHighlightGallery
            ? galleryMedium.highlight_id
            : galleryMedium.id
          context.emit("reorder", {
            id: _id,
            index: event.newIndex,
          })
          loadedGalleryMedia.value = moveElement(
            loadedGalleryMedia.value,
            event.oldIndex,
            event.newIndex
          )
        },
      })
    }

    const thumbnailDragHandle: GalleryThumbnailAction = {
      icon: mdiDrag,
      action: () => {
        //pass
      },
      class: "gallery-drag-handle",
    }

    const lightBoxService: LightboxService = inject("lightboxService")
    const eventLightboxService: EventLightboxService | null = inject(
      "EventLightboxService",
      null
    )
    const postLightboxService: PostLightboxService | null = inject(
      "PostLightboxService",
      null
    )

    /**
     *  TODO: This shouldn't be here, the logic for opening the lightbox should be in the GalleryView Component
     *  Why?
     *  - It hides the side effects of opening the lightbox from the GalleryView component
     *  - The openLightBox functionality can be refactored to not be so nested (feels like a matryoshka doll rn)
     *  - There are too many different injections and services, EventLightboxService, PostLightboxService,
     *    LightboxService, should be simplified to a single service. At the top-level component (the one that uses the GalleryView), the
     *    service should be provided with the right logic (i.e event-logic, post-logic or default logic) and the GalleryView should
     *    just inject the service and call openLightbox, trusting that the right service is injected.
     */
    const openLightBox = (galleryMedium: GalleryMedium, index: number) => {
      if (postLightboxService) {
        postLightboxService.openLightbox(index)
        return
      }
      if (eventLightboxService) {
        eventLightboxService.openLightbox(index)
        return
      }
      lightBoxService.openLightbox(galleryMedium.uploaded_file.original.url)
    }

    const loadingCount = computed(() => {
      return isLoading.value ? nextAmountOfItems.value : 0
    })

    return {
      itemSelected,
      loadedGalleryMedia,
      nextAmountOfItems,
      loadMore,
      thumbnailDragHandle,
      openLightBox,
      loadingCount,
      totalGalleryMediaCount,
      initSortIfEnabled,
    }
  },
})

function moveElement<T>(arr: T[], oldIndex: number, newIndex: number): T[] {
  if (
    oldIndex < 0 ||
    oldIndex >= arr.length ||
    newIndex < 0 ||
    newIndex >= arr.length
  ) {
    throw new Error("Index out of bounds")
  }

  const element = arr[oldIndex]
  const newArr = [...arr]
  newArr.splice(oldIndex, 1)
  newArr.splice(newIndex, 0, element)

  return newArr
}
</script>

<style>
.gallery-drag-handle {
  cursor: grab !important;
}

.gallery-drag-handle:active {
  cursor: grabbing !important;
}
</style>
